import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Plus, Trash2, Save, Info } from "lucide-react";

const inp = "w-full border border-slate-200 rounded-sm px-3 py-2 text-sm";
const numInp = "w-16 border border-slate-200 rounded-sm px-2 py-1 text-sm text-center font-data";

export default function AdminPitchCounts() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState({ division: "", label: "", age_ceiling: "", daily_max: 85, warning_at: 68 });

  const load = () => {
    setLoading(true);
    api.get("/live/pitch-count/rules")
      .then(({ data }) => setRules(data.rules || []))
      .catch(() => toast.error("Failed to load rules"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const patch = (id, updates) => {
    setRules((rs) => rs.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  };

  const save = (rule) => {
    api.patch(`/live/pitch-count/rules/${rule.id}`, {
      label: rule.label, age_ceiling: rule.age_ceiling ? Number(rule.age_ceiling) : null,
      daily_max: Number(rule.daily_max), warning_at: Number(rule.warning_at),
      rest_tiers: rule.rest_tiers, active: rule.active,
    })
      .then(() => toast.success(`${rule.division} saved`))
      .catch((e) => toast.error(e?.response?.data?.detail || "Save failed"));
  };

  const remove = (rule) => {
    if (!window.confirm(`Delete the ${rule.division} pitch-count rule?`)) return;
    api.delete(`/live/pitch-count/rules/${rule.id}`)
      .then(() => { toast.success("Rule deleted"); load(); })
      .catch(() => toast.error("Delete failed"));
  };

  const addRule = () => {
    if (!nf.division || !nf.label) return toast.error("Division and label are required");
    api.post("/live/pitch-count/rules", {
      division: nf.division, label: nf.label,
      age_ceiling: nf.age_ceiling ? Number(nf.age_ceiling) : null,
      daily_max: Number(nf.daily_max), warning_at: Number(nf.warning_at),
      rest_tiers: [{ min: 1, max: 20, rest: 0 }, { min: 21, max: 35, rest: 1 }, { min: 36, max: 999, rest: 2 }],
      active: true,
    })
      .then(() => { toast.success("Rule added"); setAdding(false); setNf({ division: "", label: "", age_ceiling: "", daily_max: 85, warning_at: 68 }); load(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Add failed"));
  };

  const setTier = (rule, i, key, val) => {
    const tiers = rule.rest_tiers.map((t, idx) => (idx === i ? { ...t, [key]: Number(val) } : t));
    patch(rule.id, { rest_tiers: tiers });
  };
  const addTier = (rule) => patch(rule.id, { rest_tiers: [...(rule.rest_tiers || []), { min: 0, max: 999, rest: 0 }] });
  const delTier = (rule, i) => patch(rule.id, { rest_tiers: rule.rest_tiers.filter((_, idx) => idx !== i) });

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="admin-pitch-counts">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-[#E63946]" />
            <h1 className="font-display text-2xl uppercase tracking-tight text-[#0A192F]">Pitch Count / Arm Safety</h1>
          </div>
          <button data-testid="add-rule-btn" onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-1 bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-sm">
            <Plus className="w-4 h-4" /> New Division
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-5 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
          Thresholds are seeded from MLB Pitch Smart / Baseball Canada guidelines and are fully editable.
          Rules are matched to a team's age division; the warning triggers an approaching-limit alert during live scoring,
          and rest tiers determine required days off after an outing.
        </p>

        {adding && (
          <div className="bg-white border border-[#E63946]/30 rounded-lg p-4 mb-5" data-testid="new-rule-form">
            <div className="grid sm:grid-cols-5 gap-3 items-end">
              <div><label className="text-[10px] uppercase tracking-wider text-slate-400">Division</label>
                <input data-testid="new-rule-division" className={inp} value={nf.division} onChange={(e) => setNf({ ...nf, division: e.target.value })} placeholder="14U" /></div>
              <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-wider text-slate-400">Label</label>
                <input data-testid="new-rule-label" className={inp} value={nf.label} onChange={(e) => setNf({ ...nf, label: e.target.value })} placeholder="14U — Age 13-14" /></div>
              <div><label className="text-[10px] uppercase tracking-wider text-slate-400">Age (U)</label>
                <input className={inp} value={nf.age_ceiling} onChange={(e) => setNf({ ...nf, age_ceiling: e.target.value })} placeholder="14" /></div>
              <button data-testid="save-new-rule-btn" onClick={addRule} className="bg-[#E63946] text-white text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-sm">Add</button>
            </div>
          </div>
        )}

        {loading ? <div className="py-16 text-center text-sm text-slate-400">Loading rules…</div> : (
          <div className="space-y-4">
            {rules.map((r) => (
              <div key={r.id} className={`bg-white border rounded-lg p-4 ${r.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`rule-${r.division}`}>
                <div className="flex flex-wrap items-center gap-3 justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-lg text-[#0A192F]">{r.division}</span>
                    <input className="border border-slate-200 rounded-sm px-2 py-1 text-sm w-64" value={r.label || ""} onChange={(e) => patch(r.id, { label: e.target.value })} />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                      <input type="checkbox" checked={!!r.active} onChange={(e) => patch(r.id, { active: e.target.checked })} data-testid={`rule-active-${r.division}`} /> Active
                    </label>
                    <button onClick={() => save(r)} data-testid={`save-rule-${r.division}`} className="flex items-center gap-1 bg-[#0A192F] text-white text-xs font-semibold uppercase px-3 py-1.5 rounded-sm"><Save className="w-3.5 h-3.5" /> Save</button>
                    <button onClick={() => remove(r)} className="text-slate-400 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-5 mb-3">
                  <Field label="Daily Max"><input className={numInp} value={r.daily_max} data-testid={`daily-max-${r.division}`} onChange={(e) => patch(r.id, { daily_max: e.target.value })} /></Field>
                  <Field label="Warning At"><input className={numInp} value={r.warning_at} data-testid={`warning-at-${r.division}`} onChange={(e) => patch(r.id, { warning_at: e.target.value })} /></Field>
                  <Field label="Age Ceiling (U)"><input className={numInp} value={r.age_ceiling || ""} onChange={(e) => patch(r.id, { age_ceiling: e.target.value })} /></Field>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Required Rest (days) by Pitch Count</div>
                    <button onClick={() => addTier(r)} className="text-[11px] font-semibold text-[#E63946]">+ Tier</button>
                  </div>
                  <div className="space-y-1.5">
                    {(r.rest_tiers || []).map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <input className={numInp} value={t.min} onChange={(e) => setTier(r, i, "min", e.target.value)} />
                        <span className="text-slate-400 text-xs">to</span>
                        <input className={numInp} value={t.max} onChange={(e) => setTier(r, i, "max", e.target.value)} />
                        <span className="text-slate-400 text-xs">pitches →</span>
                        <input className={numInp} value={t.rest} onChange={(e) => setTier(r, i, "rest", e.target.value)} />
                        <span className="text-slate-400 text-xs">days rest</span>
                        <button onClick={() => delTier(r, i)} className="text-slate-300 hover:text-[#E63946]"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>{children}</div>
);
